import { useCallback } from 'react';

/**
 * Global Error Handler Utility
 * 
 * Provides consistent error handling across the entire application.
 * Handles API errors, network errors, and displays appropriate toast notifications.
 * 
 * @param {Error} error - The error object from catch block
 * @param {Function} showToast - The showToast function from useToast hook
 * @param {string} defaultMessage - Default error message to show if error doesn't have a specific message
 * @param {Object} options - Additional options for error handling
 * @param {boolean} options.logError - Whether to log the error to console (default: true)
 * @param {boolean} options.showToast - Whether to show toast notification (default: true)
 * 
 * @example
 * import { handleApiError } from '../utils/errorHandler';
 * import useToast from '../hooks/Toast';
 * 
 * const { showToast } = useToast();
 * 
 * try {
 *   await Http.GET('/api/data');
 * } catch (error) {
 *   handleApiError(error, showToast, "Failed to fetch data");
 * }
 */
export const handleApiError = (error, showToast, defaultMessage = "An error occurred. Please try again.", options = {}) => {
  // Canceled/aborted requests are intentional (navigation, plan switch, superseded fetch) — never
  // toast/log them. The axios interceptor already skips them, but callers can reach here directly.
  if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
    return;
  }
  const { logError = true, showToast: shouldShowToast = true } = options;

  // Log error if enabled. Status + URL only — never the response body (may contain PII/financial data).
  if (logError) {
    if (error.response) {
      console.log("http logger - API error:", error.response.status, error.config?.url);
    } else {
      console.log("http logger - network error:", error.code || error.message, error.config?.url);
    }
  }

  // Don't show toast if disabled or if showToast is not provided (logging-only mode)
  if (!shouldShowToast || !showToast) {
    return; // Still logs the error above if logError is true
  }

  // Skip if the axios interceptor already surfaced a toast for this error
  if (error._tpxToastHandled === true) {
    return;
  }

  // Handle response errors (API errors)
  if (error.response) {
    const status = error.response.status;
    const errorData = error.response.data;
    const messageFromBody = (data) => {
      if (data == null) return null;
      if (typeof data === 'string' && data.trim() !== '') return data;
      if (typeof data === 'object') {
        // Many endpoints use `message` / `error`; migration & others use `errorMessage`
        const m =
          (data.errorMessage != null && String(data.errorMessage).trim() !== ''
            ? String(data.errorMessage).trim()
            : null) ||
          (data.message != null && String(data.message).trim() !== ''
            ? String(data.message).trim()
            : null) ||
          (data.error != null && String(data.error).trim() !== ''
            ? String(data.error).trim()
            : null);
        return m || null;
      }
      return null;
    };

    // Handle 503 Service Unavailable (common pattern in the app)
    if (status === 503) {
      const errorMessage = messageFromBody(errorData) || 'Service unavailable. Please retry later.';
      showToast(errorMessage, 'error');
      return;
    }

    // Handle 401 Unauthorized (already handled by Http interceptor, but can show custom message)
    if (status === 401) {
      const errorMessage = messageFromBody(errorData) || 'Unauthorized. Please log in again.';
      showToast(errorMessage, 'error');
      return;
    }

    // Handle 403 Forbidden — global interceptor owns toast UX
    if (status === 403) {
      return;
    }

    // Handle 400 Bad Request
    if (status === 400) {
      const errorMessage = messageFromBody(errorData) || defaultMessage;
      showToast(errorMessage, 'error');
      return;
    }

    // Handle 404 Not Found
    if (status === 404) {
      const errorMessage = messageFromBody(errorData) || 'Resource not found.';
      showToast(errorMessage, 'error');
      return;
    }

    // Handle 409 Conflict — prefer API message (e.g. COA still references sub-departments)
    if (status === 409) {
      const errorMessage = messageFromBody(errorData) || defaultMessage;
      showToast(errorMessage, 'error');
      return;
    }

    // Handle 500 Internal Server Error
    if (status === 500) {
      const errorMessage = messageFromBody(errorData) || 'Internal server error. Please try again later.';
      showToast(errorMessage, 'error');
      return;
    }

    // Handle other 4xx/5xx errors
    if (status >= 400 && status < 500) {
      const errorMessage = messageFromBody(errorData) || defaultMessage;
      showToast(errorMessage, 'error');
      return;
    }

    if (status >= 500) {
      const errorMessage = messageFromBody(errorData) || 'Server error. Please try again later.';
      showToast(errorMessage, 'error');
      return;
    }

    // Fallback for any other response error
    const errorMessage = messageFromBody(errorData) || defaultMessage;
    showToast(errorMessage, 'error');
    return;
  }

  // Handle network errors (no response received)
  if (error.request) {
    showToast('Network error. Please check your connection and try again.', 'error');
    return;
  }

  // Handle other errors
  showToast(defaultMessage, 'error');
};

/**
 * Creates a curried version of handleApiError with showToast pre-bound
 * Useful for useCallback hooks where you want to avoid including showToast in dependencies
 * 
 * @param {Function} showToast - The showToast function from useToast hook
 * @returns {Function} Curried error handler function
 * 
 * @example
 * const { showToast } = useToast();
 * const handleError = useErrorHandler(showToast);
 * 
 * try {
 *   await Http.GET('/api/data');
 * } catch (error) {
 *   handleError(error, "Failed to fetch data");
 * }
 */
export const useErrorHandler = (showToast) => {
  return useCallback((error, defaultMessage, options) => {
    return handleApiError(error, showToast, defaultMessage, options);
  }, [showToast]);
};

/**
 * React Hook that automatically provides error handler with showToast
 * This eliminates the need to pass showToast every time
 * 
 * @returns {Function} Error handler function that's ready to use
 * 
 * @example
 * import { useApiErrorHandler } from '../Utils/errorHandler';
 * import useToast from '../hooks/Toast';
 * 
 * const MyComponent = () => {
 *   const { showToast } = useToast();
 *   const handleError = useApiErrorHandler(showToast);
 * 
 *   const fetchData = async () => {
 *     try {
 *       await Http.GET('/api/data');
 *     } catch (error) {
 *       handleError(error, "Failed to fetch data");
 *     }
 *   };
 * 
 *   return <div>...</div>;
 * };
 */
export const useApiErrorHandler = (showToast) => {
  return useCallback((error, defaultMessage = "An error occurred. Please try again.", options = {}) => {
    return handleApiError(error, showToast, defaultMessage, options);
  }, [showToast]);
};

export default handleApiError;

